import { convexQuery } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import type { ElevenLabsVoice } from '@cvx/features/jobs/elevenlabs_voices'
import { cn } from '@rekrytering/ui/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { useAction, useMutation } from 'convex/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type VoiceFilters = {
  language: string
  accent: string
  category: string
  gender: string
  age: string
}

const EMPTY_FILTERS: VoiceFilters = {
  language: '',
  accent: '',
  category: '',
  gender: '',
  age: '',
}

export function VoiceSelector({
  jobId,
  selectedVoiceId,
  isLocked,
}: {
  jobId: Id<'jobs'>
  selectedVoiceId: string | undefined
  isLocked: boolean
}) {
  const { t } = useTranslation()
  const seedCache = useAction(
    api.features.jobs.elevenlabs_voices.refreshElevenLabsVoicesCache,
  )
  const updateJob = useMutation(api.features.jobs.jobs.updateMyJob)

  const { data: cachedVoices, isLoading } = useQuery(
    convexQuery(api.features.jobs.elevenlabs_voices.listElevenLabsVoices, {}),
  )

  const [filters, setFilters] = useState<VoiceFilters>(EMPTY_FILTERS)
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    if (cachedVoices === null) {
      seedCache({})
    }
  }, [cachedVoices, seedCache])

  const voices: ElevenLabsVoice[] = cachedVoices ?? []

  const filterOptions = useMemo(() => {
    const languages = new Set<string>()
    const accents = new Set<string>()
    const categories = new Set<string>()
    const genders = new Set<string>()
    const ages = new Set<string>()

    for (const voice of voices) {
      categories.add(voice.category)
      if (voice.labels.gender) genders.add(voice.labels.gender)
      if (voice.labels.age) ages.add(voice.labels.age)
      for (const vl of voice.verifiedLanguages) {
        languages.add(vl.language)
        if (vl.accent) accents.add(vl.accent)
      }
    }

    return {
      languages: [...languages].sort(),
      accents: [...accents].sort(),
      categories: [...categories].sort(),
      genders: [...genders].sort(),
      ages: [...ages].sort(),
    }
  }, [voices])

  const filteredVoices = useMemo(() => {
    return voices.filter((voice) => {
      if (filters.category && voice.category !== filters.category) return false
      if (filters.gender && voice.labels.gender !== filters.gender) return false
      if (filters.age && voice.labels.age !== filters.age) return false
      if (filters.language || filters.accent) {
        const hasMatchingLanguage = voice.verifiedLanguages.some(
          (vl) =>
            (!filters.language || vl.language === filters.language) &&
            (!filters.accent || vl.accent === filters.accent),
        )
        if (!hasMatchingLanguage) return false
      }
      return true
    })
  }, [voices, filters])

  const handlePlayPreview = (voice: ElevenLabsVoice): void => {
    const previewUrl =
      voice.verifiedLanguages.find((vl) => vl.previewUrl)?.previewUrl ??
      voice.previewUrl

    if (!previewUrl) return

    if (playingVoiceId === voice.voiceId) {
      audioRef.current?.pause()
      setPlayingVoiceId(null)
      return
    }

    audioRef.current?.pause()
    const audio = new Audio(previewUrl)
    audioRef.current = audio
    audio.onended = () => setPlayingVoiceId(null)
    audio.play()
    setPlayingVoiceId(voice.voiceId)
  }

  const handleSelectVoice = async (voiceId: string): Promise<void> => {
    await updateJob({ jobId, voiceId })
  }

  const setFilter = (key: keyof VoiceFilters, value: string): void => {
    setFilters((prev) => ({ ...prev, [key]: value }))
  }

  if (isLoading || cachedVoices === null) {
    return (
      <div className="flex items-center justify-center py-24 text-primary/40">
        <p className="text-sm">{t('jobs.voice.loading')}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {isLocked && (
        <div className="flex items-center gap-2 rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-800 dark:border-orange-800/40 dark:bg-orange-900/20 dark:text-orange-400">
          <span>🔒</span>
          <span>{t('jobs.locked.notice')}</span>
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        <FilterSelect
          label={t('jobs.voice.filterLanguage')}
          value={filters.language}
          options={filterOptions.languages}
          onChange={(v) => setFilter('language', v)}
        />
        <FilterSelect
          label={t('jobs.voice.filterAccent')}
          value={filters.accent}
          options={filterOptions.accents}
          onChange={(v) => setFilter('accent', v)}
        />
        <FilterSelect
          label={t('jobs.voice.filterCategory')}
          value={filters.category}
          options={filterOptions.categories}
          onChange={(v) => setFilter('category', v)}
        />
        <FilterSelect
          label={t('jobs.voice.filterGender')}
          value={filters.gender}
          options={filterOptions.genders}
          onChange={(v) => setFilter('gender', v)}
        />
        <FilterSelect
          label={t('jobs.voice.filterAge')}
          value={filters.age}
          options={filterOptions.ages}
          onChange={(v) => setFilter('age', v)}
        />
      </div>

      {filteredVoices.length === 0 ? (
        <div className="flex items-center justify-center py-24 text-primary/40">
          <p className="text-sm">{t('jobs.voice.noResults')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filteredVoices.map((voice) => (
            <VoiceCard
              key={voice.voiceId}
              voice={voice}
              isSelected={selectedVoiceId === voice.voiceId}
              isPlaying={playingVoiceId === voice.voiceId}
              isLocked={isLocked}
              onPlay={() => handlePlayPreview(voice)}
              onSelect={() => handleSelectVoice(voice.voiceId)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: string[]
  onChange: (value: string) => void
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-border bg-background px-3 py-1.5 text-sm text-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
    >
      <option value="">{label}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  )
}

function VoiceCard({
  voice,
  isSelected,
  isPlaying,
  isLocked,
  onPlay,
  onSelect,
}: {
  voice: ElevenLabsVoice
  isSelected: boolean
  isPlaying: boolean
  isLocked: boolean
  onPlay: () => void
  onSelect: () => void
}) {
  const hasPreview =
    voice.previewUrl !== null ||
    voice.verifiedLanguages.some((vl) => vl.previewUrl !== null)

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-lg border p-4 transition-colors',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/40',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-medium text-primary">{voice.name}</span>
          <span className="text-xs capitalize text-primary/50">
            {voice.category}
          </span>
        </div>
        <div className="flex gap-1.5">
          {voice.labels.gender && <Badge>{voice.labels.gender}</Badge>}
          {voice.labels.age && <Badge>{voice.labels.age}</Badge>}
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {voice.verifiedLanguages.map((vl) => (
          <Badge key={`${vl.language}-${vl.accent}`}>
            {vl.language}
            {vl.accent ? ` · ${vl.accent}` : ''}
          </Badge>
        ))}
      </div>

      <div className="flex gap-2">
        {hasPreview && (
          <button
            type="button"
            onClick={onPlay}
            className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs text-primary/70 transition hover:border-primary/40 hover:text-primary"
          >
            {isPlaying ? '⏹' : '▶'} {isPlaying ? 'Stop' : 'Preview'}
          </button>
        )}
        <button
          type="button"
          onClick={isLocked ? undefined : onSelect}
          disabled={isLocked && !isSelected}
          className={cn(
            'flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition',
            isSelected
              ? 'bg-primary text-primary-foreground'
              : isLocked
                ? 'cursor-not-allowed border border-border text-primary/30'
                : 'border border-border text-primary/70 hover:border-primary/40 hover:text-primary',
          )}
        >
          {isSelected ? 'Selected' : 'Select'}
        </button>
      </div>
    </div>
  )
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-full bg-primary/8 px-2 py-0.5 text-xs capitalize text-primary/60">
      {children}
    </span>
  )
}
